import { ButtonGroup, Divider, HStack, Text, useColorMode } from '@chakra-ui/react'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { selectAuthenticatedUser, useSelector } from '@missionlabs/api'
import { Body, IconButton, useToast } from '@missionlabs/react'
import { CameraIcon } from '@missionlabs/react/circleloop'
import {
    SendIconSolid,
    SmileEmojiPlusIconRegular,
    UploadIconRegular
} from '@missionlabs/react/zeta'
import { WhatsAppBusinessTemplate } from '@missionlabs/types'
import { TextContentListener } from 'lexical/LexicalEditor'
import { FC, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TOGGLE_EMOJI_PICKER_COMMAND } from 'shared/components/Lexical/plugins/EmojiPickerPlugin'
import {
    dispatchWhatsAppUploadEvent,
    isFileTooLarge
} from 'shared/components/Lexical/plugins/WhatsAppUploadPlugin'
import { useWhatsAppSendMessage } from 'shared/hooks/useWhatsAppSendMessage'
import { v4 } from 'uuid'

import {
    SEND_MESSAGE_COMMAND,
    WHATSAPP_CHARACTER_LIMIT,
    WHATSAPP_MAX_MESSAGE_LENGTH
} from './SMSSendMessagePlugin'

interface WhatsAppMessageToolbarProps {
    channelID: string
    limitExceeded?: boolean
    template?: WhatsAppBusinessTemplate
}

export const WhatsAppMessageToolbar: FC<WhatsAppMessageToolbarProps> = ({
    channelID,
    limitExceeded,
    template
}) => {
    const { t } = useTranslation()
    const { toast } = useToast()
    const { colorMode } = useColorMode()
    const [editor] = useLexicalComposerContext()
    const [canSend, setCanSend] = useState(false)

    const user = useSelector(selectAuthenticatedUser)
    const { draftMessage } = useWhatsAppSendMessage()

    const hasAttachment = !!draftMessage.attachment
    const canSendMedia = !template

    const imageInputRef = useRef<HTMLInputElement>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file || !user) return

        const sizeExceededError = isFileTooLarge(file)
        if (sizeExceededError) {
            toast({ title: sizeExceededError, status: 'error' })
            e.target.value = ''
            return
        }

        const reader = new FileReader()
        reader.onload = () => {
            dispatchWhatsAppUploadEvent({
                ID: v4(),
                mime: file.type,
                body: reader.result as string,
                fileName: file.name,
                userID: user.userID,
                channelID
            })
        }
        reader.readAsDataURL(file)

        e.target.value = ''
    }

    const [totalCharCount, setTotalCharacterCount] = useState(0)
    const charCount = totalCharCount % WHATSAPP_MAX_MESSAGE_LENGTH

    useEffect(() => {
        const handleContentChange: TextContentListener = content => {
            setTotalCharacterCount(content.length)
            const canSendMessage = content.length > 0 || hasAttachment
            setCanSend(canSendMessage)
        }

        const teardown = editor.registerTextContentListener(handleContentChange)
        return () => teardown()
    }, [editor, setTotalCharacterCount, setCanSend, hasAttachment])

    useEffect(() => {
        if (hasAttachment) {
            setCanSend(true)
            return
        }

        editor.getEditorState().read(() => {
            const root = editor.getEditorState()._nodeMap.get('root')
            const hasText = root?._children && root._children.length > 0
            setCanSend(hasText || false)
        })
    }, [hasAttachment, editor])

    const renderCharCount = () => {
        if (totalCharCount >= WHATSAPP_CHARACTER_LIMIT) {
            return (
                <Body size="sm" variant="bold" as="span" color={`${colorMode}.alerts.red`}>
                    Character Limit Reached
                </Body>
            )
        }

        return `${charCount} / ${WHATSAPP_MAX_MESSAGE_LENGTH}`
    }

    return (
        <HStack
            spacing={0}
            py="8px"
            w="full"
            h="full"
            justify="space-between"
            data-testid="chat-toolbar"
        >
            <HStack spacing="8px">
                <ButtonGroup spacing={0}>
                    <IconButton
                        size="sm"
                        variant="ghost"
                        aria-label="Add emoji"
                        onClick={() => {
                            editor.dispatchCommand(TOGGLE_EMOJI_PICKER_COMMAND, undefined)
                        }}
                    >
                        <SmileEmojiPlusIconRegular boxSize="24px" p="4px" />
                    </IconButton>

                    {canSendMedia && (
                        <>
                            <input
                                ref={imageInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handleFileUpload}
                                style={{ display: 'none' }}
                            />
                            <IconButton
                                size="sm"
                                variant="ghost"
                                aria-label="Send Image"
                                onClick={() => imageInputRef.current?.click()}
                                isDisabled={!user}
                            >
                                <CameraIcon boxSize="24px" p="3px" />
                            </IconButton>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*,audio/*,video/*,.pdf,.doc,.docx,.txt"
                                onChange={handleFileUpload}
                                style={{ display: 'none' }}
                            />
                            <IconButton
                                size="sm"
                                variant="ghost"
                                aria-label="Send File"
                                onClick={() => fileInputRef.current?.click()}
                                isDisabled={!user}
                            >
                                <UploadIconRegular boxSize="23px" p="3px" />
                            </IconButton>
                        </>
                    )}
                </ButtonGroup>
                <Divider orientation="vertical" h="32px" />
                {limitExceeded && (
                    <Text fontSize="sm" color={'red.500'}>
                        {t('sms.limitError')}
                    </Text>
                )}
            </HStack>
            <HStack justify="end" gap="8px" alignItems="center">
                <Text fontSize="xs">{renderCharCount()}</Text>

                <IconButton
                    size="sm"
                    variant="ghost"
                    isActive={canSend}
                    isDisabled={!canSend}
                    aria-label="Send message"
                    onClick={() => editor.dispatchCommand(SEND_MESSAGE_COMMAND, undefined)}
                    pl="4px"
                >
                    <SendIconSolid boxSize="24px" p="4px" />
                </IconButton>
            </HStack>
        </HStack>
    )
}
